import CryptoJS from 'crypto-js'

import { APP_VERSION } from '@/config/appVersion'
import { getAppDomainPreset, normalizeAppRuntimeEnv } from '@/config/domainPresets'
import { API_ENDPOINTS } from '@/config/apiEndpoints'
import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import {
  buildAppReturnUrl,
  clearAuthCallbackParamsFromCurrentUrl,
  readOAuthCallbackPayloadFromLocation,
} from '@/features/auth/callback'

export type OAuthWebLoginResult = {
  sessionId: string
  uid: number
  nickName: string
  account: string
}

type OAuthWebLoginParams = {
  returnTo: string
}

const REQUEST_TTL_MS = 5 * 60 * 1000
const REDIRECT_SESSION_KEY = 'avgmaker-oauth-redirect'
const domains = getAppDomainPreset(normalizeAppRuntimeEnv(import.meta.env.MODE || import.meta.env.VITE_APP_ENV))
const passportEndpoints = API_ENDPOINTS.passport
const cgEndpoints = API_ENDPOINTS.cg

type OAuthRedirectSession = {
  state: string
  codeVerifier: string
  createdAt: number
}

/**
 * 将字节数组编码为 OAuth PKCE 使用的 base64url 字符串。
 * @param bytes 原始字节数组。
 * @returns 去掉填充字符且替换 URL 不安全字符后的 base64url 文本。
 */
function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

/**
 * 生成安全随机 token。
 * @param byteLength 随机字节长度。
 * @returns base64url 编码后的随机 token。
 * @throws 当前浏览器不支持安全随机数时抛出错误。
 */
function createRandomToken(byteLength: number): string {
  const bytes = new Uint8Array(byteLength)
  const webCrypto = globalThis.crypto
  if (!webCrypto?.getRandomValues) {
    throw new Error('当前浏览器不支持安全随机数，无法发起授权登录')
  }
  webCrypto.getRandomValues(bytes)
  return base64Url(bytes)
}

/**
 * 向 URL 追加查询参数。
 * @param url 原始 URL。
 * @param params 需要追加的参数键值。
 * @returns 带新查询参数的 URL。
 */
function appendQuery(url: string, params: Record<string, string>): string {
  const query = Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
  return `${url}${url.includes('?') ? '&' : '?'}${query}`
}

/**
 * 计算字符串的 SHA-256 并转为 base64url。
 * @param value 待计算的明文。
 * @returns PKCE code_challenge 使用的摘要文本。
 */
async function sha256Base64Url(value: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle?.digest) {
    const digest = CryptoJS.SHA256(CryptoJS.enc.Utf8.parse(value))
    return CryptoJS.enc.Base64.stringify(digest).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
  }
  const bytes = new TextEncoder().encode(value)
  const digest = await subtle.digest('SHA-256', bytes)
  return base64Url(new Uint8Array(digest))
}

/**
 * 读取 fetch 响应为 JSON，并在 HTTP 失败时抛出可读错误。
 * @param response fetch Response 对象。
 * @returns JSON 对象；空响应或解析失败时返回 null。
 * @throws HTTP 状态非 2xx 时抛出响应里的错误文案。
 */
async function readJsonOrThrow(response: Response) {
  const rawText = (await response.text()).trim()
  let payload: Record<string, unknown> | null = null
  try {
    payload = rawText ? JSON.parse(rawText) as Record<string, unknown> : null
  } catch {
    payload = null
  }
  if (!response.ok) {
    const message = String(payload?.error || rawText || `HTTP ${response.status}`).trim()
    throw new Error(message)
  }
  return payload
}

/**
 * 从候选值中取第一个非空字符串。
 * @param values 候选值列表。
 * @returns 去除首尾空白后的文本；没有可用值时返回空字符串。
 */
function pickString(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (text) return text
  }
  return ''
}

/**
 * 将候选值转换为正整数。
 * @param value 候选值。
 * @returns 有效正整数；无效时返回 0。
 */
function toPositiveInt(value: unknown): number {
  const num = Number(value ?? 0)
  return Number.isFinite(num) && num > 0 ? Math.floor(num) : 0
}

/**
 * 从 exchange_session 返回中提取 session_id。
 * @param payload 接口原始返回。
 * @returns session_id；没有可用字段时返回空字符串。
 */
function extractSessionId(payload: unknown) {
  const record = payload as Record<string, unknown> | null
  const data = record?.data as Record<string, unknown> | undefined
  return pickString(record?.session_id, record?.sessionId, data?.session_id, data?.sessionId)
}

/**
 * 从 exchange_session 返回中提取用户 uid。
 * @param payload 接口原始返回。
 * @returns 正整数 uid；没有可用字段时返回 0。
 */
function extractUid(payload: unknown) {
  const record = payload as Record<string, unknown> | null
  const data = record?.data as Record<string, unknown> | undefined
  return toPositiveInt(record?.uid ?? data?.uid ?? data?.user_id ?? data?.userId)
}

/**
 * 从 exchange_session 返回中提取用户昵称。
 * @param payload 接口原始返回。
 * @returns 用户昵称；没有可用字段时返回空字符串。
 */
function extractNickName(payload: unknown) {
  const record = payload as Record<string, unknown> | null
  const data = record?.data as Record<string, unknown> | undefined
  return pickString(record?.nick_name, record?.nickName, data?.nick_name, data?.nickName, data?.nickname, data?.nick)
}

/**
 * 从 exchange_session 返回中提取登录账号。
 * @param payload 接口原始返回。
 * @returns 登录账号；没有可用字段时返回空字符串。
 */
function extractAccount(payload: unknown) {
  const record = payload as Record<string, unknown> | null
  const data = record?.data as Record<string, unknown> | undefined
  return pickString(record?.account, data?.account, data?.username, data?.user_name, data?.login_name, data?.uname)
}

/**
 * 构造 Passport OAuth 授权页地址。
 * @param params state、PKCE code_challenge 与回跳地址。
 * @returns 可用于整页跳转的授权 URL。
 */
function buildAuthorizeUrl(params: {
  state: string
  codeChallenge: string
  redirectUri: string
}): string {
  const authorizeUrl = `${domains.passport.replace(/\/+$/g, '')}/${passportEndpoints.oauthAuthorize.replace(/^\/+/g, '')}`
  return appendQuery(authorizeUrl, {
    redirect_mode: 'redirect',
    redirect_uri: params.redirectUri,
    state: params.state,
    code_challenge: params.codeChallenge,
    code_challenge_method: 'S256',
  })
}

/**
 * 保存本次 OAuth 跳转前的临时校验信息。
 * @param session state、codeVerifier 和创建时间。
 */
function saveRedirectSession(session: OAuthRedirectSession) {
  sessionStorage.setItem(REDIRECT_SESSION_KEY, JSON.stringify(session))
}

/**
 * 读取本次 OAuth 跳转前保存的临时校验信息。
 * @returns 校验信息完整时返回 session，否则返回 null。
 */
function loadRedirectSession(): OAuthRedirectSession | null {
  const raw = sessionStorage.getItem(REDIRECT_SESSION_KEY)
  if (!raw) return null
  try {
    const data = JSON.parse(raw) as Partial<OAuthRedirectSession>
    const state = pickString(data.state)
    const codeVerifier = pickString(data.codeVerifier)
    const createdAt = Number(data.createdAt ?? 0)
    if (!state || !codeVerifier || !Number.isFinite(createdAt)) return null
    return { state, codeVerifier, createdAt }
  } catch {
    return null
  }
}
/**
 * 清理 OAuth 跳转临时校验信息。
 */
function clearRedirectSession() {
  sessionStorage.removeItem(REDIRECT_SESSION_KEY)
}

/**
 * 使用授权码和 PKCE verifier 向 CG 换取工具 session。
 * @param toolAuthCode Passport 回调带回的授权码。
 * @param codeVerifier 发起登录前生成的 PKCE verifier。
 * @returns 登录快照所需的 sessionId、uid、昵称和账号。
 */
async function exchangeSession(toolAuthCode: string, codeVerifier: string) {
  const webUrl = new BaseWebUrl()
  const exchangeUrl = `${String(webUrl.baseCg).replace(/\/+$/g, '')}/${cgEndpoints.exchangeSession.replace(/^\/+/g, '')}`
  const body = new URLSearchParams({
    tool_auth_code: toolAuthCode,
    code_verifier: codeVerifier,
    tool_cur_ver: APP_VERSION,
    channel_id: '0',
  })
  const response = await fetch(exchangeUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: body.toString(),
  })
  const payload = await readJsonOrThrow(response)
  if (Number(payload?.ret ?? 0) !== 1) {
    throw new Error(pickString(payload?.msg, 'exchange_session 返回失败'))
  }
  const sessionId = extractSessionId(payload)
  if (!sessionId) {
    throw new Error('exchange_session 未返回有效 session_id')
  }
  return {
    sessionId,
    uid: extractUid(payload),
    nickName: extractNickName(payload),
    account: extractAccount(payload),
  }
}

/**
 * 按整页跳转模式发起 Passport 授权。
 * @param params 登录回跳参数。
 */
export async function startOAuthWebLogin(params: OAuthWebLoginParams): Promise<void> {
  const state = createRandomToken(24)
  const codeVerifier = createRandomToken(48)
  const codeChallenge = await sha256Base64Url(codeVerifier)
  const returnTo = params.returnTo
  const redirectUri = buildAppReturnUrl(returnTo)
  saveRedirectSession({
    state,
    codeVerifier,
    createdAt: Date.now(),
  })
  window.location.href = buildAuthorizeUrl({ state, codeChallenge, redirectUri })
}

/**
 * 读取整页跳转回调参数并换取 session_id。
 * @returns 没有 OAuth 回调时返回 null；有回调时返回登录快照。
 */
export async function resolveOAuthRedirectLogin(): Promise<OAuthWebLoginResult | null> {
  const callback = readOAuthCallbackPayloadFromLocation()
  if (!callback) return null
  clearAuthCallbackParamsFromCurrentUrl()
  const session = loadRedirectSession()
  clearRedirectSession()
  if (callback.error) {
    throw new Error(callback.error)
  }
  if (!session) {
    throw new Error('授权会话已失效，请重新点击登录')
  }
  if (Date.now() - session.createdAt > REQUEST_TTL_MS) {
    throw new Error('授权超时，请重新点击登录')
  }
  if (callback.state !== session.state) {
    throw new Error('授权状态校验失败，请重新点击登录')
  }
  const toolAuthCode = pickString(callback.toolAuthCode)
  if (!toolAuthCode) {
    throw new Error('授权回调缺少授权码')
  }
  const exchanged = await exchangeSession(toolAuthCode, session.codeVerifier)
  return {
    sessionId: exchanged.sessionId,
    uid: exchanged.uid,
    nickName: exchanged.nickName,
    account: exchanged.account,
  }
}

